/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * Provides utilities for generating PKCE parameters required for secure OAuth2 flows.
 * PKCE prevents authorization code interception attacks by using a dynamically generated
 * code verifier and challenge pair.
 */

export interface PKCEParams {
  codeVerifier: string;
  codeChallenge: string;
}

/**
 * Generates PKCE parameters for OAuth2 authentication
 *
 * Creates a cryptographically secure code verifier and its corresponding SHA256
 * code challenge as required by RFC 7636 (PKCE specification).
 *
 * @returns Promise containing the code verifier and challenge
 */
export const generatePKCE = async (): Promise<PKCEParams> => {
  // Generate random code verifier (43-128 chars, base64url encoded)
  // Using 32 random bytes provides sufficient entropy (256 bits)
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);

  // Convert to base64url encoding (RFC 4648 Section 5)
  const codeVerifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-') // Replace + with -
    .replace(/\//g, '_') // Replace / with _
    .replace(/=/g, ''); // Remove padding

  // Generate code challenge (SHA256 hash of verifier, base64url encoded)
  const buffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  return { codeVerifier, codeChallenge };
};
